import { mapToResolve } from "@angular/router";

import { NotificationsService } from "@hermes/aphrodite-layout-state";
import {
    canMatchCartDeprecated,
    canMatchCartRedesignPage,
} from "@hermes/domains-cart-pages-cart-page";

import {
    ResetPasswordGuard,
    ResetPasswordService,
} from "@hermes/domains-customer-pages-reset-password";
import { canMatchAgecPage } from "@hermes/hermes-web-fragments-agec-page-matcher";
import {
    EmptyComponentComponent,
    ErrorPageComponent,
    NotFoundPageComponent,
} from "@hermes/core-router-components";
import { ShopsService } from "@hermes/domains-shop-fragments-shop-utils";
import { ShopAssetUrlPipe, StorePageResolver } from "@hermes/domains-shop-pages-store-page";
import {
    HermesRoutes,
    LAYOUT_MARGIN_ADAPTATIVE,
    LAYOUT_MARGIN_FIXE,
    SHELL_APPEARANCE_LIGHT,
    SHELL_APPEARANCE_LIGHT_FOOTER_ONLY,
    SHELL_APPEARANCE_NONE,
} from "@hermes/utils-generic-constants";
import { hermesRouteTitle } from "@hermes/utils-generic-helpers";
import { ImageUrlPipe } from "@hermes/utils-generic-pipes-image-url";

export const APP_CHILD_ROUTES: HermesRoutes[] = [
    {
        path: "care-and-use",
        loadChildren: () => import("@hermes/domains-user-guides-pages-user-guides"),
    },
    {
        path: "register/unsubscribe/",
        loadComponent: () =>
            import("@hermes/domains-register-your-interest-pages-register-your-interest-unsubscribe-page").then(
                (m) => m.RegisterYourInterestUnsubscribeComponent,
            ),
        data: {
            layoutSystem: LAYOUT_MARGIN_FIXE,
        },
    },
    // product page
    {
        path: "product",
        loadChildren: () =>
            import("@hermes/domains-product-details-pages-product-page"),
    },
    // Home page
    {
        path: "",
        pathMatch: "full",
        loadChildren: () => import("@hermes/domains-story-pages-home-page"),
    },
    // Grid page (Category)
    {
        path: "category",
        loadChildren: () =>
            import("@hermes/domains-product-listing-pages-product-listing"),
    },
    // Grid page (Search)
    {
        path: "search",
        loadChildren: () =>
            import("@hermes/domains-product-listing-pages-product-listing"),
    },
    // Product personalization generic page
    {
        path: "product/:productCategory/config/:slug/",
        loadChildren: () =>
            import("@hermes/domains-product-details-pages-product-personalization-page"),
    },
    {
        path: "personalization-landing-page",
        loadChildren: () =>
            import("@hermes/domains-product-details-pages-product-personalization-page").then(
                (m) => m.PERSO_LANDING_PAGE_ROUTES,
            ),
    },
    // Cart page
    {
        path: "cart",
        canMatch: [canMatchCartRedesignPage()],
        loadChildren: () =>
            import("@hermes/domains-cart-pages-cart-page").then((m) => m.routes),

        data: {
            shell: SHELL_APPEARANCE_LIGHT_FOOTER_ONLY,
        },
    },
    {
        path: "cart/",
        canMatch: [canMatchCartDeprecated()],
        loadChildren: () =>
            import("@hermes/domains-cart-legacy-pages-cart-page").then(
                (m) => m.CartPageModule,
            ),
        data: {
            shell: SHELL_APPEARANCE_LIGHT,
            layoutSystem: LAYOUT_MARGIN_ADAPTATIVE,
        },
    },
    // Login page
    {
        path: "login/",
        loadChildren: () => import("@hermes/hermes-web-pages-login-page"),
        data: {
            layoutSystem: LAYOUT_MARGIN_ADAPTATIVE,
        },
    },
    // Checkout login page
    {
        path: "checkout/login/",
        loadChildren: () => import("@hermes/hermes-web-pages-checkout-login-page"),
        data: {
            shell: SHELL_APPEARANCE_LIGHT,
            layoutSystem: LAYOUT_MARGIN_ADAPTATIVE,
        },
    },
    // Wechat redirection page
    {
        path: "checkout/login/wechat-redirect/",
        loadChildren: () => import("@hermes/hermes-web-pages-wechat-redirect-page"),
        data: {
            shell: SHELL_APPEARANCE_LIGHT,
            layoutSystem: LAYOUT_MARGIN_ADAPTATIVE,
        },
    },
    // Account creation page
    {
        path: "account/create/",
        loadChildren: () => import("@hermes/hermes-web-pages-account-creation-page"),
        data: {
            layoutSystem: LAYOUT_MARGIN_ADAPTATIVE,
        },
    },
    // Account page
    {
        path: "account",
        loadChildren: () => import("@hermes/hermes-web-pages-account-page"),
    },
    // Checkout page
    {
        path: "checkout/",
        loadChildren: () =>
            import("@hermes/domains-cart-legacy-pages-checkout-page").then(
                (m) => m.CHECKOUT_PAGE_ROUTES,
            ),
        data: {
            shell: SHELL_APPEARANCE_LIGHT,
            layoutSystem: LAYOUT_MARGIN_ADAPTATIVE,
        },
    },
    // Contact us page
    {
        path: "contact-us/",
        loadChildren: () => import("@hermes/hermes-web-pages-contact-us-page"),
        data: {
            layoutSystem: LAYOUT_MARGIN_ADAPTATIVE,
        },
    },
    // Order confirmation page
    {
        path: "order/:orderId/",
        loadChildren: () =>
            import("@hermes/domains-cart-legacy-pages-order-confirmation-page").then(
                (r) => r.ORDER_CONFIRMATION_PAGE_ROUTES,
            ),
        data: {
            shell: SHELL_APPEARANCE_LIGHT,
            backButton: "home",
            layoutSystem: LAYOUT_MARGIN_ADAPTATIVE,
        },
    },
    // Change password page
    {
        path: "change-password/",
        loadChildren: () => import("@hermes/hermes-web-pages-change-password-page"),
        data: {
            layoutSystem: LAYOUT_MARGIN_ADAPTATIVE,
        },
    },
    // reset-password page
    {
        path: "reset/",
        providers: [
            NotificationsService,
            ResetPasswordService,
            ResetPasswordGuard,
        ],
        canActivate: [ResetPasswordGuard],
        title: hermesRouteTitle(
            $localize`:@@hermes-global-translations.reset-password:Reset password`,
        ),
        loadComponent: () =>
            import("@hermes/domains-customer-pages-reset-password").then(
                (m) => m.ResetPasswordComponent,
            ),
        data: {
            layoutSystem: LAYOUT_MARGIN_ADAPTATIVE,
        },
    },
    {
        path: "payment-validation",
        loadChildren: () => import("@hermes/hermes-web-pages-payment-validation-page"),
        data: {
            shell: SHELL_APPEARANCE_NONE,
        },
    },
    {
        path: "find-store/:country/:city/:id/",
        loadComponent: () =>
            import("@hermes/domains-shop-pages-store-page").then(
                (m) => m.StorePageContainerComponent,
            ),
        providers: [
            StorePageResolver,
            ShopsService,
            ShopAssetUrlPipe,
            ImageUrlPipe,
        ],
        resolve: { store: mapToResolve(StorePageResolver) },
    },
    {
        path: "find-store/:country/:city/:id/appointment/",
        loadChildren: () => import("@hermes/domains-shop-pages-appointment-in-store-page"),
    },
    {
        path: "find-store",
        loadChildren: () => import("@hermes/domains-shop-pages-shop-locator-page"),
    },
    // Story page from Discovery
    {
        path: "content",
        loadChildren: () => import("@hermes/domains-story-pages-story-page"),
    },
    // Preview page from Discovery
    {
        path: "preview-cms/content",
        loadChildren: () => import("@hermes/domains-story-pages-preview-page"),
    },
    // Story page from Discovery (only for test)
    {
        path: "discovery",
        loadChildren: () => import("@hermes/domains-story-pages-story-page"),
    },
    // After sales page
    {
        path: "aftersales",
        loadChildren: () =>
            import("@hermes/domains-after-sales-pages-after-sales-page").then(
                (m) => m.AFTER_SALES_ROUTES,
            ),
    },
    // Agec page
    {
        path: "environment",
        loadChildren: () =>
            import("@hermes/domains-agec-pages-agec-page").then(
                (m) => m.AGEC_PAGE_ROUTES,
            ),
        canMatch: [canMatchAgecPage()],
    },
    // Legal Page
    {
        path: "legal",
        loadChildren: () => import("@hermes/domains-legal-pages-legal-page"),
        data: {
            layoutSystem: LAYOUT_MARGIN_ADAPTATIVE,
        },
    },
    // Faq page
    {
        path: "faq",
        loadChildren: () => import("@hermes/domains-faq-pages-faq-page"),
    },
    // Fragrance finder page
    {
        path: "fragrance-finder",
        loadChildren: () =>
            import("@hermes/domains-fragrance-finder-pages-fragrance-finder-page"),
    },
    // Empty page (for SSR)
    {
        path: "empty-page",
        component: EmptyComponentComponent,
    },
    // 404 Page
    {
        path: "404-error",
        component: NotFoundPageComponent,
    },
    // Back in stock unsubscribe page
    {
        path: "back-in-stock/unsubscribe/",
        loadChildren: () =>
            import("@hermes/domains-back-in-stock-pages-back-in-stock-unsubscribe-page"),
    },
    // Sitemap page
    {
        path: "sitemap/",
        loadComponent: () =>
            import("@hermes/domains-sitemap-pages-sitemap").then(
                (m) => m.SitemapPageComponent,
            ),
    },
    // 500 page
    {
        path: "500-error",
        component: ErrorPageComponent,
    },
    // Unmatched : redirect to 404 page
    {
        path: "**",
        redirectTo: "404-error",
    },
];
